import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
  type Lobby,
  type LobbyGame,
  type Studio,
  TileType,
} from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import App from '../../app';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import placeholderStyles from '../../styles/placeholder.css?inline';
import style from './providers-list-page.css?inline';
import '../../_ui-core_/components/ui-section-header/ui-section-header';

const CName = 'providers-list-page';

// WeakMap pattern: Chrome retains IntersectionObserver callbacks even after
// disconnect(). Using a module-level function with a WeakMap avoids closing
// over `this`, allowing the component to be GC'd independently.
const _observerHosts = new WeakMap<IntersectionObserver, WeakRef<ProvidersListPage>>();

function handleIntersection(this: IntersectionObserver, entries: IntersectionObserverEntry[]) {
  const ref = _observerHosts.get(this);
  const host = ref?.deref();
  if (!host) return;
  host._onIntersection(entries);
}

@customElement(CName)
export class ProvidersListPage extends LitElement {
  static readonly styles = [unsafeCSS(placeholderStyles), unsafeCSS(style)];

  @state() private _gamesMap: { [key: string]: LobbyGame[] } = {};
  @state() private _studios: Studio[];
  @state() private readonly _visibleCarousels = new Set<string>();

  private _observer: IntersectionObserver;
  private _gamesByProviderTaskId: number | null = null;

  private readonly _elementMap = new Map<Element, string>();
  private readonly _subHelper = new SubHelper();

  private readonly _updateGamesMap = (lobby: Lobby | undefined) => {
    if (!lobby) return;
    this._updateLobby(lobby);
    this._getGamesByAllProviders();
  };

  /** @internal Called by the module-level IntersectionObserver callback (WeakMap pattern). */
  public _onIntersection(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const studioId = this._elementMap.get(entry.target);
        if (studioId) {
          this._visibleCarousels.add(studioId);
          this._observer.unobserve(entry.target);
          this._elementMap.delete(entry.target);
          this.requestUpdate();
        }
      }
    }

    // Disconnect observer once all studios are visible
    if (this._studios?.length === this._visibleCarousels.size) {
      this._observer.disconnect();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    // Block content rendering until games-by-provider data arrives.
    // The task is always added — removeTask is called once _gamesMap is populated.
    // This prevents layout shift (footer pushed down) on both cold landing
    // and in-app navigation where games still need to be fetched.
    this._gamesByProviderTaskId = window.$app.renderService.addTask('games-by-provider');
    this._initializeSubscriptions();
    this._initializeObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanupResources();
  }

  firstUpdated() {
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: window.$app.$t.get(I18n.base.providers),
      trackingSource: TrackingEventSource.PROVIDERS,
      isGoBackShown: true,
    });
  }

  render() {
    return html`
      <main>
        <ui-a11y-title>${window.$app.$t.get(I18n.a11y.heading.providers)}</ui-a11y-title>
        ${this._renderStudios()}
      </main>
    `;
  }

  private _renderStudios() {
    return this._studios?.map((studio) => this._renderStudio(studio));
  }

  private _renderStudio(studio: Studio) {
    const games = this._gamesMap[studio.filterId] || [];
    if (games.length === 0) {
      window.$app.logger.log(`Hide studio with zero games: '${studio.name}' (filterId: ${studio.filterId})`);
      return nothing;
    }

    return html` ${this._renderSectionHeader(studio)} ${this._renderGameCarousel(studio.filterId, games)} `;
  }

  private _renderSectionHeader(studio: Studio) {
    const iconUrl = studio.icon ?? studio.img ?? undefined;
    const description = `${studio.gamesTotalCount} ${window.$app.$t.get(I18n.base.games)}`;

    return html`
      <ui-section-header
        .action=${{
          callback: () => this._navigateToProvider(studio.slug),
          text: window.$app.$t.get(I18n.base.viewAll),
        }}
        class="nested"
        description=${description}
        iconUrl=${ifDefined(iconUrl)}
        title=${studio.name}
        ?enLangAttr=${true}
      ></ui-section-header>
    `;
  }

  private _renderGameCarousel(studioId: string, games: LobbyGame[]) {
    const isAwaitingGames = this._visibleCarousels.has(studioId) && !games;
    return html`
      <div id="studio-${studioId}" class="games">
        ${isAwaitingGames ? this._renderPlaceholders() : this._renderActualGameCarousel(games)}
      </div>
    `;
  }

  private _renderActualGameCarousel(games: LobbyGame[]) {
    return html`
      <ui-games-carousel
        class="carousel"
        .games=${games}
        .trackingSource=${TrackingEventSource.PROVIDERS}
        padding="var(--page-padding-inline)"
      ></ui-games-carousel>
    `;
  }

  private _renderPlaceholders() {
    return html`
      <div class="game-carousel-placeholder">
        ${Array.from({ length: 4 }, () => html`<ui-placeholder></ui-placeholder>`)}
      </div>
    `;
  }

  private _initializeSubscriptions() {
    this._subHelper.addSub(App.instance.content.getLobby(), this._updateGamesMap, true);
  }

  private _initializeObserver() {
    this._observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '200px',
      threshold: 0,
    });
    _observerHosts.set(this._observer, new WeakRef(this));
  }

  private _cleanupResources() {
    this._observer.disconnect();
    _observerHosts.delete(this._observer);
    this._elementMap.clear();
    this._subHelper.unsubscribeAll();
  }

  private async _updateLobby(lobby: Lobby) {
    this._studios = lobby.studios;
    await this.updateComplete;
    if (!this.isConnected) return;
    lobby.studios.forEach((studio) => {
      const element = this.renderRoot.querySelector(`#studio-${studio.filterId}`);
      if (element && !this._elementMap.has(element)) {
        this._observer.observe(element);
        this._elementMap.set(element, studio.filterId);
      }
    });
  }

  private async _getGamesByAllProviders() {
    const games = await App.instance.content.getGamesByAllProviders(TileType.P1);
    if (!this.isConnected) return;
    this._gamesMap = games;
    this._trackGamesImpression();
    // Wait for Lit to commit the new _gamesMap to DOM before removing
    // the render task — prevents the splash from hiding before content is painted.
    if (this._gamesByProviderTaskId !== null) {
      await this.updateComplete;
      this._gamesByProviderTaskId = window.$app.renderService.removeTask(this._gamesByProviderTaskId);
    }
  }

  private _navigateToProvider(tag: string) {
    App.instance.router.navigateToProvider(tag);
  }

  private _trackGamesImpression() {
    const mappedCategories = this._studios.map((studio) => {
      const gameSlugs = (this._gamesMap[studio?.filterId ?? ''] || []).map((game) => game.slug);
      return { location: studio.slug, gameSlugs };
    });

    window.$app.vaix.trackGamesImpressionVaix(mappedCategories, true);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: ProvidersListPage;
  }
}
